/**
 * **GM OVERRIDE** (`decisions/0209`, `projects/gm-override-mode.md`): the browser half of the GM's
 * sticky override mode. While the GM has it on, every unit's command menu opens enabled
 * ({@link OVERRIDE_MENU_STATE}), every forceable row lifts (`commandMenuTree`'s `override`),
 * out-of-range and illegal targets stay clickable ({@link liftsTargetGate}), and every action
 * intent the app sends goes out wrapped in `gm_force` ({@link forcingEncounter}). The server is the
 * wall: a player can't send one, and what it can't force it refuses with the reason.
 */
import { isForceableIntent } from "@lancer-vtt/rules";
import { err, ok, type EncounterIntent, type RequestMove, type WireCell } from "@lancer-vtt/shared";
import type { ActionDraft } from "../../../lib/encounter/combatInspector";
import type { CommandMenuState } from "../../../lib/encounter/commandMenuActions";
import type { UseEncounter } from "../../../lib/encounter/useEncounter";

/** Every unit's menu while the override is on: enabled, whatever its turn state. */
export const OVERRIDE_MENU_STATE: CommandMenuState = { kind: "enabled", headline: "OVERRIDE" };

/** `intent` wrapped in `gm_force` when the override can force it; a reaction or an end of turn goes as it is. */
export function forcedIntent(intent: EncounterIntent): EncounterIntent {
  return isForceableIntent(intent) ? { type: "gm_force", intent } : intent;
}

/**
 * `encounter` with its `sendIntent` forcing every forceable action (`decisions/0209`), and its
 * `dispatchMove` sending a forced move: no local dry-run and no Boost fold, since the move is off
 * every move rule; the room's walk keeps the structure (a route, a cell to stop on) and refuses with
 * the reason otherwise.
 */
export function forcingEncounter(encounter: UseEncounter): UseEncounter {
  const sendIntent = (intent: EncounterIntent) => encounter.sendIntent(forcedIntent(intent));
  return {
    ...encounter,
    sendIntent,
    dispatchMove: (combatantId, to, opts = {}) => {
      const move: RequestMove = {
        type: "request_move",
        combatantId,
        to: to as WireCell,
        ...(opts.path !== undefined ? { path: opts.path as WireCell[] } : {}),
      };
      return sendIntent(move) ? ok(undefined) : err("encounter room not connected");
    },
  };
}

/** The aims whose target filter the override lifts: every one that picks a unit for a forceable action. */
export function liftsTargetGate(aim: ActionDraft): boolean {
  if (aim.mode === "skirmish") return aim.reaction !== true;
  return aim.mode !== "release" && aim.mode !== "prepare";
}

/** The banner across the board while the override is on. */
export function OverrideBanner() {
  return (
    <div className="combat-override-banner" role="status" aria-label="GM override on">
      <span className="combat-override-banner-word">GM OVERRIDE</span>
      <span className="combat-override-banner-line">RULES OFF · ANY UNIT · ANY ACTION · ANY TARGET</span>
    </div>
  );
}
